import * as z from "zod";
import { SectionType } from "../enums/database-enums";
import { ExposedPropsSchema } from "../primitives/editor";
import {
  StorefrontPageSectionDataSchema,
  StorefrontPageSectionHiddenOnSchema,
  StorefrontPageSectionSchema,
} from "./section-base";

export const StorefrontCraftSerializedNodeSchema = z
  .object({
    type: z.union([z.string(), z.object({ resolvedName: z.string() })]),
    displayName: z.string(),
    isCanvas: z.boolean(),
    parent: z.union([z.string(), z.null(), z.undefined()]).transform((value) => value ?? null),
    linkedNodes: z.record(z.string(), z.string()),
    nodes: z.array(z.string()),
    hidden: z.boolean(),
    hiddenOn: z.optional(StorefrontPageSectionHiddenOnSchema).optional(),
    custom: z.record(z.string(), z.unknown()).optional(),
    props: z.record(z.string(), z.unknown()),
    _childCanvas: z.record(z.string(), z.string()).optional(),
  })
  .catchall(z.unknown());

export type StorefrontCraftSerializedNode = z.infer<typeof StorefrontCraftSerializedNodeSchema>;

export const StorefrontCraftSerializedNodesSchema = z.record(z.string(), StorefrontCraftSerializedNodeSchema);

export type StorefrontCraftSerializedNodes = z.infer<typeof StorefrontCraftSerializedNodesSchema>;

export const StorefrontPageGenericSectionDataSchema = StorefrontPageSectionDataSchema.extend({
  name: z
    .string()
    .regex(/^[a-zA-Z0-9]+$/)
    .optional()
    .describe(
      "The name of the section comprised of uppercase, lowercase or numeric characters only (match regex ^[a-zA-Z0-9]+$)",
    ),
  props: z.record(z.string(), z.any()).optional(),
  exposedProps: ExposedPropsSchema.optional().describe(
    "Generic section additional exposed props for user input used in custom logic",
  ),
  customLogic: z
    .string()
    .optional()
    .describe(
      "Custom logic that must be a JavaScript function string that receives `{ React, runtime, props, helpers, hooks }`",
    ),
  jsx: z.string().describe("The JSX content of the section"),
  craftNodes: StorefrontCraftSerializedNodesSchema.optional(),
  isStickyToTop: z.boolean().optional(),
  isStickyToBottom: z
    .boolean()
    .optional()
    .describe("Whether this Generic section sticks to the bottom while scrolling"),
});

export type StorefrontPageGenericSectionData = z.infer<typeof StorefrontPageGenericSectionDataSchema>;

export const StorefrontGenericSectionSchema = StorefrontPageSectionSchema.extend({
  type: z.literal(SectionType.GENERIC),
  data: StorefrontPageGenericSectionDataSchema,
});

export type StorefrontGenericSection = z.infer<typeof StorefrontGenericSectionSchema>;
